import {
  Table,
  Popover,
  NumberInput,
  Stack,
  Notification,
  LoadingOverlay,
  Center,
  Group,
  Button,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import React from "react";
import { useState } from "react";
import { CurrencyDollar, Hash } from "tabler-icons-react";
import { IconCheck, IconX } from "@tabler/icons";
import { getOrderHandler } from "../../../services/orderHandlers";
import { SYMBOLS } from "../../../constants/symbols";
import { buildApiUrl } from "../../../config/endpoints";
import "./OpenOrdersTableGenerator.css";

export default function OpenOrdersTableGenerator({
  owner,
  DataObject,
  setAsks,
  setBids,
  setClosed,
  title,
  symbol,
  side,
}) {
  const visibility = {};
  for (let i = 0; i < DataObject.length; i++) {
    visibility[DataObject[i].orderid] = false;
  }

  const ordersPerPage = 7;
  const [visible, setVisible] = useState(visibility);
  const [isLoading, setIsLoading] = useState(false);
  const [sortDir, setSortDir] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const symbolObj = SYMBOLS.find((x) => x.code === symbol);
  const [notification, setNotification] = useState(null);

  const form = useForm({
    initialValues: {
      price: 12,
      qty: 5,
    },
  });

  const modifyOrderHandler = async (
    symbol,
    side,
    orderId,
    prevQuantity,
    prevPrice,
    newQuantity,
    newPrice
  ) => {
    setIsLoading(true);
    setNotification(
      <Notification
        loading
        title="Sending modify order request to server"
        disallowClose
      >
        Please wait until order is modified
      </Notification>
    );
    try {
      const response = await fetch(buildApiUrl('/order'), {
        method: "PUT",
        body: JSON.stringify({
          symbol: symbol,
          side: side,
          order_id: orderId,
          prev_quantity: prevQuantity,
          prev_price: prevPrice,
          new_quantity: newQuantity,
          new_price: newPrice,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (response.status === 420) {
        throw new Error(`Insufficient Funds!`);
      } else if (response.status === 421) {
        throw new Error(`Insufficient Assets!`);
      } else if (!response.ok) {
        throw new Error(`Error! status: ${response.status}`);
      }

      await response.json();
      setNotification(
        <Notification
          icon={<IconCheck size={18} />}
          color="teal"
          title="Modify order successful"
          onClose={() => {
            setNotification(null);
          }}
        >
          Order is modified and added to order book
        </Notification>
      );
      setTimeout(() => setNotification(null), 5000);
      // console.log("result is: ", JSON.stringify(result, null, 4));
    } catch (err) {
      console.log(err.message);
      setNotification(
        <Notification
          icon={<IconX size={18} />}
          color="red"
          onClose={() => {
            setNotification(null);
          }}
        >
          {err.message}
        </Notification>
      );
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsLoading(false);
      getOrderHandler(owner, symbol, setAsks, setBids, setClosed);
    }
  };

  const cancelOrderHandler = async (symbol, side, price, orderId) => {
    setIsLoading(true);
    setNotification(
      <Notification
        loading
        title="Sending cancel order request to server"
        disallowClose
      >
        Please wait until order is canceled
      </Notification>
    );
    try {
      const response = await fetch(buildApiUrl('/order'), {
        method: "DELETE",
        body: JSON.stringify({
          symbol: symbol,
          side: side,
          order_id: orderId,
          price: price,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Error! status: ${response.status}`);
      }

      await response.json();
      setNotification(
        <Notification
          icon={<IconCheck size={18} />}
          color="teal"
          title="Cancel order successful"
          onClose={() => {
            setNotification(null);
          }}
        >
          Order is canceled and removed from the order book
        </Notification>
      );
      setTimeout(() => setNotification(null), 5000);
      // console.log("result is: ", JSON.stringify(result, null, 4));
    } catch (err) {
      console.log(err.message);
      setNotification(
        <Notification
          icon={<IconX size={18} />}
          color="red"
          onClose={() => {
            setNotification(null);
          }}
        >
          {err.message}
        </Notification>
      );
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsLoading(false);
      getOrderHandler(owner, symbol, setAsks, setBids, setClosed);
    }
  };

  const formatDate = (value) => {
    if (value == null) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "Asia/Singapore",
    };
    const formattedDate = date.toLocaleString("en-SG", options);
    const formattedTime = date.toLocaleTimeString("en-SG", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      timeZone: "Asia/Singapore",
    });
    return (
      <div style={{ textAlign: "center" }}>
        {formattedDate}
        <br />
        {formattedTime}
      </div>
    );
  };

  const formatNumber = (value) => {
    if (value == null || value === "") return "—";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return String(value);
    const parts = num.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const columns = [
    { key: "quantity",     header: "Quantity",      render: (v) => formatNumber(v) },
    { key: "price",        header: "Price",          render: (v) => formatNumber(v) },
    { key: "openquantity", header: "Open Quantity",  render: (v) => formatNumber(v) },
    { key: "fillcost",     header: "Fill Cost",      render: (v) => formatNumber(v) },
    { key: "createdat",    header: "Created At",     render: (v) => v != null ? formatDate(v) : "—" },
    { key: "updatedat",    header: "Updated At",     render: (v) => v != null ? formatDate(v) : "—" },
  ];

  const sortedData = [...DataObject].sort((a, b) => {
    const tA = a.createdat ?? 0;
    const tB = b.createdat ?? 0;
    return sortDir === "asc" ? tA - tB : tB - tA;
  });

  const totalPages = Math.max(1, Math.ceil(sortedData.length / ordersPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = sortedData.slice((safePage - 1) * ordersPerPage, safePage * ordersPerPage);

  const handleSortDir = (dir) => { setSortDir(dir); setCurrentPage(1); };

  return (
    <>
      <LoadingOverlay visible={isLoading} overlayBlur={2} />
      {notification}
      <Group spacing={4} mb={6}>
        <span style={{ fontSize: 12, color: "#888" }}>Time:</span>
        <Button size="xs" compact variant={sortDir === "desc" ? "filled" : "subtle"} onClick={() => handleSortDir("desc")}>↓ Newest</Button>
        <Button size="xs" compact variant={sortDir === "asc" ? "filled" : "subtle"} onClick={() => handleSortDir("asc")}>↑ Oldest</Button>
      </Group>
      <Table verticalSpacing="xs" highlightOnHover>
        <thead>
          <tr>
            <th colSpan={columns.length + 1}>
              <Center>{title}</Center>
            </th>
          </tr>
          <tr key={"header"}>
            {columns.map((col) => (
              <th key={col.key} style={{ textAlign: "center", minWidth: "120px" }}>
                <Center>{col.header}</Center>
              </th>
            ))}
            <th>
              <Center>Operations</Center>
            </th>
          </tr>
        </thead>
        <tbody>
          {paginatedData.map((item) => (
            <tr key={item.orderid}>
              {columns.map((col) => (
                <td key={col.key}>
                  <Center>{col.render(item[col.key])}</Center>
                </td>
              ))}
              <td> 
                <Center>
                  <Popover
                    opened={visible[item.orderid]}
                    onClose={() =>
                      setVisible((visible) => ({
                        ...visible,
                        [item.orderid]: false,
                      }))
                    }
                    position="right"
                    placement="end"
                    withCloseButton
                    transition="pop-top-right"
                    closeOnOutsideClick
                    closeButtonLabel="Close"
                    closeOnClickOutside
                    closeOnEscape
                    gutter={10}
                    zIndex={1000}
                    overlay={<div style={{ padding: "10px" }}>{/* ... */}</div>}
                  >
                    <Popover.Target>
                      <button
                        className="modify-button"
                        onClick={() => {
                          form.setValues({ price: Number(item.price), qty: Number(item.quantity) });
                          setVisible((visible) => ({
                            ...visible,
                            [item.orderid]: !visible[item.orderid],
                          }));
                        }}
                      >
                        Modify
                      </button>
                    </Popover.Target>
                    <Popover.Dropdown>
                      <Stack spacing="sm">
                        {item.price !== "Market" && (
                          <NumberInput
                            label="New Price"
                            precision={2}
                            min={0.01}
                            step={0.01}
                            max={1000000}
                            icon={<CurrencyDollar size={18} />}
                            {...form.getInputProps("price")}
                          />
                        )}
                        <NumberInput
                          label="New Quantity"
                          precision={symbolObj.precision}
                          min={symbolObj.min}
                          step={symbolObj.step}
                          max={100}
                          icon={<Hash size={18} />}
                          {...form.getInputProps("qty")}
                        />
                        <button
                          className="submit-button"
                          onClick={() => {
                            modifyOrderHandler(
                              symbol,
                              side,
                              item.orderid,
                              item.quantity,
                              item.price,
                              form.values.qty,
                              form.values.price
                            );
                          }}
                        >
                          Submit
                        </button>
                      </Stack>
                    </Popover.Dropdown>
                  </Popover>

                  <button
                    className="cancel-button"
                    onClick={() => {
                      cancelOrderHandler(
                        symbol,
                        side,
                        item.price,
                        item.orderid
                      );
                    }}
                  >
                    Cancel
                  </button>
                </Center>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 8 }}>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "4px 10px", cursor: "pointer" }}>Previous</button>
          <span>Page {safePage} / {totalPages}</span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "4px 10px", cursor: "pointer" }}>Next</button>
        </div>
      )}
    </>
  );
}
